import { ArrowUpRight, Box, Cpu, CreditCard, Flag } from 'lucide-react';

import { Dialog, Skeleton, useDialog, useDrawer } from '@components/primitives';

import { TierBadge } from '@components/features/Admin/TierBadge';
import { Command } from '@components/features/CommandMenu';
import { NavigationDropdown } from '@components/features/Layout/Navigation/Dropdown';
import { avatar } from '@utils/generateAvatar';

import { EnclaveUsageLimitUpdate } from './EnclaveUsageLimitUpdate';
import { FeatureToggle } from './FeatureToggle';
import { ResourceUsageLimitUpdateModal } from './ResourceUsageLimitUpdate';
import { TeamPaymentUpdate } from './TeamPaymentUpdate';
import { AdminTeamObject } from './types';

import css from './styles.module.css';

export function TeamActions({
  team,
  children,
}: {
  team: AdminTeamObject;
  children: React.ReactNode;
}) {
  const resourceUsageDialog = useDialog();
  const paymentsDialog = useDialog();
  const enclaveLimitDialog = useDialog();
  const featureDrawer = useDrawer();

  return (
    <>
      <Dialog state={resourceUsageDialog}>
        <Dialog.Header title={`Resource Usage Limits for ${team?.name}`} />
        <Dialog.Body>
          <ResourceUsageLimitUpdateModal teamUuid={team?.uuid} />
        </Dialog.Body>
      </Dialog>

      <Dialog state={paymentsDialog}>
        <Dialog.Header title={`Payments for ${team?.name}`} />
        <Dialog.Body>
          <TeamPaymentUpdate team={team} />
        </Dialog.Body>
      </Dialog>

      <Dialog state={enclaveLimitDialog}>
        <Dialog.Header title={`Enclave Limits for ${team?.name}`} />
        <Dialog.Body>
          <EnclaveUsageLimitUpdate team={team} onSubmit={() => {}} />
        </Dialog.Body>
      </Dialog>

      <FeatureToggle state={featureDrawer} team={team} />

      <NavigationDropdown trigger={children}>
        <NavigationDropdown.Section>
          <NavigationDropdown.Item
            icon={
              <span className={css.teamAvatar}>
                <img src={avatar(team?.name ?? '')} alt="" />
              </span>
            }
            label={
              team ? (
                <span className={css.teamName}>{team.name}</span>
              ) : (
                <Skeleton width={96} height={12} />
              )
            }
            suffix={<TierBadge team={team} />}
          />

          <NavigationDropdown.Item
            icon={<ArrowUpRight />}
            label="Open team"
            to={`/${team.uuid}`}
          />
          <NavigationDropdown.Item
            icon={<Flag />}
            label="Features"
            onClick={featureDrawer.open}
          />
          <NavigationDropdown.Item
            icon={<CreditCard />}
            label="Upgrade tier"
            onClick={paymentsDialog.open}
          />
          <NavigationDropdown.Item
            icon={<Box />}
            label="Enclave limits"
            onClick={enclaveLimitDialog.open}
          />
          <NavigationDropdown.Item
            icon={<Cpu />}
            label="Resource usage limits"
            onClick={resourceUsageDialog.open}
          />
        </NavigationDropdown.Section>
      </NavigationDropdown>

      <Command
        type="route"
        id="team-open"
        label="Open team"
        icon={<ArrowUpRight />}
        context="team"
        group={team.name}
        href={`/${team.uuid}`}
      />

      <Command
        type="action"
        id="team-manage-features"
        label="Manage features"
        icon={<Flag />}
        context="team"
        group={team.name}
        onSelect={featureDrawer.open}
      />

      <Command
        type="action"
        id="team-upgrade-tier"
        label="Upgrade tier"
        icon={<CreditCard />}
        context="team"
        group={team.name}
        onSelect={paymentsDialog.open}
      />

      <Command
        type="action"
        id="team-enclave-limits"
        label="Enclave limits"
        icon={<Box />}
        context="team"
        group={team.name}
        onSelect={enclaveLimitDialog.open}
      />

      <Command
        type="action"
        id="team-resource-usage-limits"
        label="Resource usage limits"
        icon={<Cpu />}
        context="team"
        group={team.name}
        onSelect={resourceUsageDialog.open}
      />
    </>
  );
}
